import { useRef } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { loadStripe } from '@stripe/stripe-js'

import { useCheckPageData } from '@/hooks/checkPageData/useCheckPageData'

import { getStripeWalletButtonStyles } from '@/config/stripe'
import {
  TSelectionPage,
  TBodyParamsPage,
  TEventDatePage,
  TInfoPage,
  TInputPage,
} from '@/config/types'
import { PageName } from '@/enums/pageName'
import { getPageName, getPath } from '@/utils/urlUtils'

import { Spinner } from '@/components/spinner'

import { AlreadyHasAccountPage } from '@/commonPages/alreadyHasAccountPage'
import { AlreadyPaidPage } from '@/commonPages/alreadyPaidPage'
import { HeightPage } from '@/commonPages/bodyPages/heightPage'
import { WeightPage } from '@/commonPages/bodyPages/weightPage'
import { CustomPage } from '@/commonPages/customPage'
import { EventDate } from '@/commonPages/eventDate'
import { InfoPage } from '@/commonPages/infoPage'
import { InputPage } from '@/commonPages/inputPage'
import { MultiSelectPage } from '@/commonPages/multiSelectPage'
import { OneSelectPage } from '@/commonPages/oneSelectPage'
import { PaymentFailPage } from '@/commonPages/paymentFailPage'
import { PaymentFailLTPPage } from '@/commonPages/paymentFailPage/ltp'
import { OrgfitPaymentSuccessfulPage } from '@/commonPages/paymentSuccessfulPage'
import { PreLTPPage } from '@/commonPages/preLTPPage'
import { FakeRegistrationPage } from '@/commonPages/registrationPage/fake'
import { SelectLTPPage } from '@/commonPages/selectLTP'
import { SelectPlanPage } from '@/commonPages/selectPlan'
import { VerificationPageV2 } from '@/commonPages/verificationPage'
import { WeightLossProgram } from '@/commonPages/weightLossProgram'
import { useCommonLogic } from '@/core/commonLogic'
import { useSession } from '@/core/session/useSession'
import { Profile } from '@/orgfit/pages/profile'

import { getCommonSurveyPageProps } from './routeUtils'
import {
  ltpSelectPage,
  ltpPaymentScreenPage,
  preLTPPage,
  ltpPaymentFailPage,
} from '../config/pages/ltp'
import { pagesOrder } from '../config/pages/pageOrder'
import {
  paymentFailPage,
  paymentScreenPage,
  paymentSuccessPage,
  selectPlanPage,
  alreadyHasAccountPage,
  alreadyPaidPage,
} from '../config/pages/payments'
import {
  weightLossProgram,
  preparingPlanPage,
  profilePage,
  warmUpPage,
} from '../config/pages/preparingPlan'
import { registrationPage } from '../config/pages/registration'
import { pagesConfig } from '../config/pages/selectionPages'
import { verificationPage } from '../config/pages/verification'
import { paypalButtonConfigStyle, stripeFormAppearance } from '../config/paymentEngineFormApperance'
import { getItem, removeItem, setItem, setLastVisitedItem } from '../config/storage'

const stripePromise = loadStripe(window.config.STRIPE_PUBLISHED_KEY)

export const OrgfitRoutes = () => {
  const sessionContext = useSession()
  const { pathname } = useLocation()
  const pageName = getPageName(pathname)
  const redirectToSelectPlan = useRef<boolean>(false)

  const isRedirectToLastVisitedPage = useCheckPageData(pageName, pagesOrder, getItem)

  useCommonLogic(sessionContext)

  const isRegistrationEnabled = window.config.CHECKOUT_SETTINGS?.registration_page_enabled

  if (!sessionContext.sessionId) {
    return <Spinner extraClassName="page-stub-spinner" />
  }

  if (redirectToSelectPlan.current) {
    redirectToSelectPlan.current = false
    return <Navigate to={getPath(PageName.selectPlan)} />
  }

  if (isRedirectToLastVisitedPage) {
    return <Navigate to={isRedirectToLastVisitedPage} />
  }

  return (
    <Routes>
      <Route path={'/'} element={<Navigate to={PageName.age} />} />
      <Route
        path={getPath(PageName.age)}
        element={
          <OneSelectPage
            pageName={PageName.age}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.age)}
          />
        }
      />
      <Route
        path={getPath(PageName.audience)}
        element={
          <InfoPage
            pageName={PageName.audience}
            {...getCommonSurveyPageProps<TInfoPage>(PageName.audience)}
          />
        }
      />
      <Route
        path={getPath(PageName.mainGoal)}
        element={
          <OneSelectPage
            pageName={PageName.mainGoal}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.mainGoal)}
          />
        }
      />
      <Route
        path={getPath(PageName.physicalBuild)}
        element={
          <OneSelectPage
            pageName={PageName.physicalBuild}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.physicalBuild)}
          />
        }
      />
      <Route
        path={getPath(PageName.dreamBody)}
        element={
          <OneSelectPage
            pageName={PageName.dreamBody}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.dreamBody)}
          />
        }
      />
      <Route
        path={getPath(PageName.areasToFocus)}
        element={
          <MultiSelectPage
            resetElementIndex={99}
            pageName={PageName.areasToFocus}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.areasToFocus)}
          />
        }
      />
      <Route
        path={getPath(PageName.trainingExperience)}
        element={
          <OneSelectPage
            pageName={PageName.trainingExperience}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.trainingExperience)}
          />
        }
      />
      <Route
        path={getPath(PageName.lastIdealWeigh)}
        element={
          <OneSelectPage
            pageName={PageName.lastIdealWeigh}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.lastIdealWeigh)}
          />
        }
      />
      <Route
        path={getPath(PageName.pilatesExperience)}
        element={
          <OneSelectPage
            pageName={PageName.pilatesExperience}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.pilatesExperience)}
          />
        }
      />
      <Route
        path={getPath(PageName.flexibility)}
        element={
          <OneSelectPage
            pageName={PageName.flexibility}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.flexibility)}
          />
        }
      />
      <Route
        path={getPath(PageName.workout)}
        element={
          <OneSelectPage
            pageName={PageName.workout}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.workout)}
          />
        }
      />
      <Route
        path={getPath(PageName.shortnessOfBreath)}
        element={
          <OneSelectPage
            pageName={PageName.shortnessOfBreath}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.shortnessOfBreath)}
          />
        }
      />
      <Route
        path={getPath(PageName.walkingFrequency)}
        element={
          <OneSelectPage
            pageName={PageName.walkingFrequency}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.walkingFrequency)}
          />
        }
      />
      <Route
        path={getPath(PageName.struggle)}
        element={
          <MultiSelectPage
            resetElementIndex={2}
            pageName={PageName.struggle}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.struggle)}
          />
        }
      />
      <Route
        path={getPath(PageName.workSchedule)}
        element={
          <OneSelectPage
            pageName={PageName.workSchedule}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.workSchedule)}
          />
        }
      />
      <Route
        path={getPath(PageName.describeYourDay)}
        element={
          <OneSelectPage
            pageName={PageName.describeYourDay}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.describeYourDay)}
          />
        }
      />
      <Route
        path={getPath(PageName.energyLevels)}
        element={
          <OneSelectPage
            pageName={PageName.energyLevels}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.energyLevels)}
          />
        }
      />
      <Route
        path={getPath(PageName.sleepRoutine)}
        element={
          <OneSelectPage
            pageName={PageName.sleepRoutine}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.sleepRoutine)}
          />
        }
      />
      <Route
        path={getPath(PageName.waterIntake)}
        element={
          <OneSelectPage
            pageName={PageName.waterIntake}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.waterIntake)}
          />
        }
      />
      <Route
        path={getPath(PageName.breakfast)}
        element={
          <OneSelectPage
            pageName={PageName.breakfast}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.breakfast)}
          />
        }
      />
      <Route
        path={getPath(PageName.lunch)}
        element={
          <OneSelectPage
            pageName={PageName.lunch}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.lunch)}
          />
        }
      />
      <Route
        path={getPath(PageName.dinner)}
        element={
          <OneSelectPage
            pageName={PageName.dinner}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.dinner)}
          />
        }
      />
      <Route
        path={getPath(PageName.diet)}
        element={
          <OneSelectPage
            pageName={PageName.diet}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.diet)}
          />
        }
      />
      <Route
        path={getPath(PageName.badHabits)}
        element={
          <MultiSelectPage
            resetElementIndex={4}
            pageName={PageName.badHabits}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.badHabits)}
          />
        }
      />
      <Route
        path={getPath(PageName.weightOnEvents)}
        element={
          <MultiSelectPage
            resetElementIndex={7}
            pageName={PageName.weightOnEvents}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.weightOnEvents)}
          />
        }
      />
      <Route
        path={getPath(PageName.height)}
        element={
          <HeightPage
            pageName={PageName.height}
            {...getCommonSurveyPageProps<TBodyParamsPage>(PageName.height)}
          />
        }
      />
      <Route
        path={getPath(PageName.weight)}
        element={
          <WeightPage
            pageName={PageName.weight}
            {...getCommonSurveyPageProps<TBodyParamsPage>(PageName.weight)}
          />
        }
      />
      <Route
        path={getPath(PageName.weightGoal)}
        element={
          <WeightPage
            pageName={PageName.weightGoal}
            {...getCommonSurveyPageProps<TBodyParamsPage>(PageName.weightGoal)}
          />
        }
      />
      <Route
        path={getPath(PageName.age2)}
        element={
          <InputPage
            pageName={PageName.age2}
            {...getCommonSurveyPageProps<TInputPage>(PageName.age2)}
          />
        }
      />
      <Route
        path={getPath(PageName.name)}
        element={
          <InputPage
            pageName={PageName.name}
            {...getCommonSurveyPageProps<TInputPage>(PageName.name)}
          />
        }
      />
      <Route
        path={getPath(PageName.profile)}
        element={
          <Profile
            key={PageName.profile}
            pageName={PageName.profile}
            setLastVisitedItem={setLastVisitedItem}
            pageConfig={profilePage}
            getItem={getItem}
            setItem={setItem}
            config={window.config}
          />
        }
      />
      <Route
        path={getPath(PageName.event)}
        element={
          <OneSelectPage
            pageName={PageName.event}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.event)}
          />
        }
      />
      <Route
        path={getPath(PageName.eventDate)}
        element={
          <EventDate
            pageName={PageName.eventDate}
            eventPageConfig={getCommonSurveyPageProps<TEventDatePage>(PageName.event).pageConfig}
            {...getCommonSurveyPageProps<TSelectionPage>(PageName.eventDate)}
          />
        }
      />

      <Route
        path={getPath(PageName.weightLossProgram)}
        element={
          <WeightLossProgram
            key={PageName.weightLossProgram}
            pageName={PageName.weightLossProgram}
            pageConfig={weightLossProgram}
            pagesConfig={pagesConfig}
            getItem={getItem}
            setLastVisitedItem={setLastVisitedItem}
            config={window.config}
          />
        }
      />
      <Route
        path={getPath(PageName.preparingPlan)}
        element={
          <CustomPage
            key={PageName.preparingPlan}
            pageName={PageName.preparingPlan}
            setLastVisitedItem={setLastVisitedItem}
            pageConfig={preparingPlanPage}
            getItem={getItem}
            config={window.config}
          />
        }
      />
      {isRegistrationEnabled && (
        <Route
          path={getPath(PageName.registration)}
          element={
            <FakeRegistrationPage
              key={PageName.emailCollection}
              pageName={PageName.emailCollection}
              setItem={setItem}
              getItem={getItem}
              setLastVisitedItem={setLastVisitedItem}
              pageConfig={registrationPage}
              config={window.config}
            />
          }
        />
      )}

      <Route
        path={getPath(PageName.warmUpScreen)}
        element={
          <InfoPage
            pageName={PageName.warmUpScreen}
            key={PageName.warmUpScreen}
            pageConfig={warmUpPage}
            setItem={setItem}
            getItem={getItem}
            setLastVisitedItem={setLastVisitedItem}
            config={window.config}
          />
        }
      />

      <Route
        path={getPath(PageName.selectPlan)}
        element={
          <SelectPlanPage
            key={PageName.selectPlan}
            pageConfig={selectPlanPage}
            setLastVisitedItem={setLastVisitedItem}
            getItem={getItem}
            setItem={setItem}
            removeItem={removeItem}
            config={window.config}
            paymentOptions={{
              pageConfig: paymentScreenPage,
              selectPlanPage,
              stripePromise,
              stripeFormAppearance,
              getStripeWalletButtonStyles,
              paypalButtonConfigStyle,
              successRedirectTo: PageName.preLtp,
              contactStateRoutesMap: {
                has_subscription: PageName.preLtp,
              },
            }}
          />
        }
      />

      <Route
        path={getPath(PageName.verificationV2)}
        element={
          <VerificationPageV2
            key={PageName.verificationV2}
            pageName={PageName.verificationV2}
            setItem={setItem}
            getItem={getItem}
            setLastVisitedItem={setLastVisitedItem}
            verificationPage={verificationPage}
            config={window.config}
          />
        }
      />

      <Route
        path={getPath(PageName.preLtp)}
        element={
          <PreLTPPage
            key={PageName.preLtp}
            pageConfig={preLTPPage}
            getItem={getItem}
            setLastVisitedItem={setLastVisitedItem}
            config={window.config}
          />
        }
      />

      <Route
        path={getPath(PageName.selectLTP)}
        element={
          <SelectLTPPage
            key={PageName.selectLTP}
            pageConfig={ltpSelectPage}
            getItem={getItem}
            setItem={setItem}
            removeItem={removeItem}
            setLastVisitedItem={setLastVisitedItem}
            config={window.config}
            paymentOptions={{
              pageConfig: ltpPaymentScreenPage,
              selectLTPPage: ltpSelectPage,
              stripePromise,
              stripeFormAppearance,
              getStripeWalletButtonStyles,
              paypalButtonConfigStyle,
              successRedirectTo: PageName.paymentSuccess,
            }}
          />
        }
      />

      <Route
        path={getPath(PageName.ltpPaymentFailed)}
        element={
          <PaymentFailLTPPage
            key={PageName.ltpPaymentFailed}
            pagesOrder={pagesOrder}
            pageConfig={ltpPaymentFailPage}
            getItem={getItem}
            config={window.config}
          />
        }
      />
      <Route
        path={getPath(PageName.paymentSuccess)}
        element={
          <OrgfitPaymentSuccessfulPage
            key={PageName.paymentSuccess}
            pageConfig={paymentSuccessPage}
            getItem={getItem}
            config={window.config}
          />
        }
      />
      <Route
        path={getPath(PageName.paymentFailed)}
        element={
          <PaymentFailPage
            key={PageName.paymentFailed}
            pagesOrder={pagesOrder}
            pageConfig={paymentFailPage}
            setItem={setItem}
            getItem={getItem}
            config={window.config}
          />
        }
      />

      <Route
        path={getPath(PageName.alreadyHasAccount)}
        element={
          <AlreadyHasAccountPage
            key={PageName.alreadyHasAccount}
            alreadyHasAccountPage={alreadyHasAccountPage}
            getItem={getItem}
            config={window.config}
          />
        }
      />
      <Route
        path={getPath(PageName.alreadyPaid)}
        element={
          <AlreadyPaidPage
            key={PageName.alreadyPaid}
            alreadyPaidPage={alreadyPaidPage}
            getItem={getItem}
            config={window.config}
          />
        }
      />

      <Route path={'*'} element={<Navigate to={PageName.age} replace={true} />} />
    </Routes>
  )
}
